const DEFAULT_START_TIME = "06:00";

export const toFiniteNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const clampNonNegativeInt = (value) => Math.max(0, Math.trunc(toFiniteNumber(value, 0)));

export const timeToMinutes = (time = DEFAULT_START_TIME) => {
  const [h, m] = String(time).split(":").map(Number);
  const hours = Number.isFinite(h) ? h : 0;
  const mins = Number.isFinite(m) ? m : 0;
  return Math.max(0, hours * 60 + mins);
};

export const minutesToTime = (totalMinutes = 0) => {
  const mins = Math.max(0, Math.trunc(totalMinutes));
  const hours = Math.floor(mins / 60) % 24;
  const remainder = mins % 60;
  return `${String(hours).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
};

export const formatMinutesToHoursMinutes = (totalMinutes = 0) => {
  const mins = clampNonNegativeInt(totalMinutes);
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;

  if (hours <= 0) return `${remainder}m`;
  if (remainder <= 0) return `${hours}h`;
  return `${hours}h ${remainder}m`;
};

export const normalizeOvenProducts = (products) => {
  if (!products) return [];

  if (Array.isArray(products)) {
    return products
      .map((entry) => {
        if (!entry || typeof entry !== "object") return null;
        const [productRef, rawConfig] = Object.entries(entry)[0] || [];
        if (!productRef) return null;
        const config = typeof rawConfig === "object" && rawConfig !== null ? rawConfig : {};
        return {
          productRef,
          piecesPerHour: toFiniteNumber(config.piecesPerHour, 0),
          capacity: toFiniteNumber(config.capacity, 0),
          bakeTimeMinutes: toFiniteNumber(config.bakeTimeMinutes, 0),
          preheatMinutes: toFiniteNumber(config.preheatMinutes, 0),
          cooldownMinutes: toFiniteNumber(config.cooldownMinutes, 0),
        };
      })
      .filter(Boolean);
  }

  if (typeof products === "object") {
    return Object.entries(products).map(([productRef, config]) => ({
      productRef,
      piecesPerHour: toFiniteNumber(config?.piecesPerHour, 0),
      capacity: toFiniteNumber(config?.capacity, 0),
      bakeTimeMinutes: toFiniteNumber(config?.bakeTimeMinutes, 0),
      preheatMinutes: toFiniteNumber(config?.preheatMinutes, 0),
      cooldownMinutes: toFiniteNumber(config?.cooldownMinutes, 0),
    }));
  }

  return [];
};

const resolveOvenProductConfig = (oven, productRef) => {
  const normalized = normalizeOvenProducts(oven?.products);
  const target = String(productRef || "");
  const plain = target.split("/").pop();
  return (
    normalized.find((entry) => {
      const ref = String(entry.productRef || "");
      return ref === target || ref.split("/").pop() === plain;
    }) || null
  );
};

export const getOvenEffectiveRate = (oven, productRef) => {
  const productConfig = resolveOvenProductConfig(oven, productRef);
  const directRate =
    toFiniteNumber(productConfig?.piecesPerHour, 0) || toFiniteNumber(oven?.throughputPerHour, 0);
  if (directRate > 0) return directRate;

  const capacity =
    toFiniteNumber(productConfig?.capacity, 0) || toFiniteNumber(oven?.capacity, 0);
  const bakeTime =
    toFiniteNumber(productConfig?.bakeTimeMinutes, 0) || toFiniteNumber(oven?.bakeTimeMinutes, 0);

  if (capacity > 0 && bakeTime > 0) {
    return (capacity / bakeTime) * 60;
  }
  return 0;
};

export const estimateBakeDurationMinutes = (oven, productRef, pieces) => {
  const totalPieces = clampNonNegativeInt(pieces);
  if (totalPieces <= 0) return 0;

  const productConfig = resolveOvenProductConfig(oven, productRef);
  const capacity =
    toFiniteNumber(productConfig?.capacity, 0) || toFiniteNumber(oven?.capacity, 0);
  const bakeTime =
    toFiniteNumber(productConfig?.bakeTimeMinutes, 0) || toFiniteNumber(oven?.bakeTimeMinutes, 0);
  const preheat =
    toFiniteNumber(productConfig?.preheatMinutes, 0) || toFiniteNumber(oven?.preheatMinutes, 0);
  const cooldown =
    toFiniteNumber(productConfig?.cooldownMinutes, 0) || toFiniteNumber(oven?.cooldownMinutes, 0);

  if (capacity > 0 && bakeTime > 0) {
    const batches = Math.ceil(totalPieces / capacity);
    if (batches <= 0) return 0;
    return Math.ceil(preheat + batches * bakeTime + Math.max(0, batches - 1) * cooldown);
  }

  const rate = getOvenEffectiveRate(oven, productRef);
  return rate > 0 ? Math.ceil((totalPieces / rate) * 60) : 0;
};

/**
 * Derives a production queue from the quantities entered in the Production List table.
 *
 * For each product with a total piece count > 0 across all selected lists/lines,
 * creates a queue entry with a stable deterministic ID so that manual oven overrides
 * (manualOvenId) can be preserved across regenerations.
 *
 * @param {object} params
 * @param {string[]} params.selectedProductionLists - IDs of selected production lists
 * @param {object} params.lineProductQuantities - nested map: listId -> lineId -> productId -> { quantity, onHand, extra, unit, sequence }
 * @param {string} [params.productionDate] - ISO date string used to scope stable IDs
 * @param {object[]} [params.existingQueue] - current queue entries; used to preserve manualOvenId
 * @returns {object[]} derived queue entries
 */
export const deriveQueueFromProductionData = ({
  selectedProductionLists = [],
  lineProductQuantities = {},
  productionDate = "",
  existingQueue = [],
}) => {
  // Aggregate total pieces per product across all selected lists and lines
  const productTotals = {};

  (selectedProductionLists || []).forEach((listId) => {
    const linesObj = lineProductQuantities[listId] || {};
    Object.entries(linesObj).forEach(([, products]) => {
      Object.entries(products || {}).forEach(([productId, data]) => {
        const pieceCount = Math.max(1, toFiniteNumber(data?.unit?.pieceCount, 1));
        const quantityNeeded = Math.max(
          0,
          clampNonNegativeInt(data?.quantity) -
            clampNonNegativeInt(data?.onHand) +
            clampNonNegativeInt(data?.extra)
        );
        const totalPieces = quantityNeeded * pieceCount;
        if (totalPieces <= 0) return;

        if (!productTotals[productId]) {
          productTotals[productId] = {
            totalPieces: 0,
            minSequence: toFiniteNumber(data?.sequence, 9999),
          };
        } else {
          productTotals[productId].minSequence = Math.min(
            productTotals[productId].minSequence,
            toFiniteNumber(data?.sequence, 9999)
          );
        }
        productTotals[productId].totalPieces += totalPieces;
      });
    });
  });

  // Sort products by sequence then alphabetically for stable ordering
  const sortedProducts = Object.entries(productTotals)
    .filter(([, info]) => info.totalPieces > 0)
    .sort((a, b) => {
      const seqDiff = a[1].minSequence - b[1].minSequence;
      return seqDiff !== 0 ? seqDiff : String(a[0]).localeCompare(String(b[0]));
    });

  // Build lookup of existing manualOvenId by stable batch ID
  const existingOverrides = {};
  (existingQueue || []).forEach((batch) => {
    if (batch.id && batch.manualOvenId) {
      existingOverrides[batch.id] = batch.manualOvenId;
    }
  });

  const datePrefix = productionDate ? String(productionDate) : "nodate";

  return sortedProducts.map(([productRef, info], index) => {
    const stableId = `auto-${datePrefix}-${String(productRef).replace(/\//g, "-")}`;
    return {
      id: stableId,
      productRef,
      quantity: info.totalPieces,
      priority: index + 1,
      manualOvenId: existingOverrides[stableId] || "",
    };
  });
};

export const buildProductionSchedule = ({ queue = [], ovens = [], startTime = DEFAULT_START_TIME }) => {
  const normalizedQueue = (queue || [])
    .map((entry, index) => ({
      id: entry?.id || `queue-${index}`,
      productRef: entry?.productRef || "",
      quantity: clampNonNegativeInt(entry?.quantity),
      priority: clampNonNegativeInt(entry?.priority || index + 1),
      manualOvenId: entry?.manualOvenId || "",
      createdIndex: index,
    }))
    .filter((entry) => entry.productRef && entry.quantity > 0)
    .sort((a, b) => a.priority - b.priority || a.createdIndex - b.createdIndex);

  const ovenState = (ovens || []).map((oven) => ({
    oven,
    busyUntil: timeToMinutes(startTime),
    busyMinutes: 0,
    itemCount: 0,
  }));

  const scheduledItems = normalizedQueue.map((entry) => {
    const byManualId = ovenState.find(({ oven }) => String(oven.id) === String(entry.manualOvenId));
    const compatible = ovenState.filter(
      ({ oven }) => getOvenEffectiveRate(oven, entry.productRef) > 0 || estimateBakeDurationMinutes(oven, entry.productRef, entry.quantity) > 0
    );
    const candidates = byManualId ? [byManualId] : compatible;

    if (candidates.length === 0) {
      return {
        ...entry,
        assignedOvenId: "",
        assignedOvenName: "Unassigned",
        startTime: "",
        endTime: "",
        durationMinutes: 0,
      };
    }

    const selected = candidates.reduce((best, current) => {
      const currentDuration = estimateBakeDurationMinutes(current.oven, entry.productRef, entry.quantity);
      const currentEnd = current.busyUntil + currentDuration;
      if (!best) return { state: current, end: currentEnd, duration: currentDuration };
      return currentEnd < best.end ? { state: current, end: currentEnd, duration: currentDuration } : best;
    }, null);

    selected.state.itemCount += 1;
    selected.state.busyMinutes += selected.duration;
    const startAt = selected.state.busyUntil;
    selected.state.busyUntil = selected.end;

    return {
      ...entry,
      assignedOvenId: selected.state.oven.id,
      assignedOvenName: selected.state.oven.name || selected.state.oven.id,
      startTime: minutesToTime(startAt),
      endTime: minutesToTime(selected.end),
      durationMinutes: selected.duration,
    };
  });

  const firstStart = timeToMinutes(startTime);
  const maxEnd = Math.max(firstStart, ...scheduledItems.map((item) => timeToMinutes(item.endTime || startTime)));
  const windowMinutes = Math.max(1, maxEnd - firstStart);
  const totalPieces = scheduledItems.reduce((sum, item) => sum + item.quantity, 0);

  const ovenMetrics = ovenState.map(({ oven, busyMinutes, itemCount }) => ({
    ovenId: oven.id,
    ovenName: oven.name || oven.id,
    busyMinutes,
    itemCount,
    utilizationPercent: Math.round((busyMinutes / windowMinutes) * 1000) / 10,
  }));

  return {
    items: scheduledItems,
    metrics: {
      totalPieces,
      totalMinutes: windowMinutes,
      overallThroughputPerHour: windowMinutes > 0 ? Math.round((totalPieces / windowMinutes) * 600) / 10 : 0,
      ovens: ovenMetrics,
    },
  };
};
